/* global globalThis */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import {
  AlertCircle,
  TrendingUp,
  Database,
  Table,
  RefreshCw,
  Filter,
  X,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Sector,
} from "recharts";
import axiosClientPayload from "../../../../api-client/AxiosClientPayload";
import axiosClient from "../../../../api-client/AxiosClient";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/cjs/styles/prism";

// ─── Color Palette per dimension ──────────────────────────────────────────────
const DIMENSION_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#06b6d4", "#f97316",
];
const getDimColor = (index) => DIMENSION_COLORS[index % DIMENSION_COLORS.length];

// ── Generate unique UUID ────────────────────────────────────────
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: use crypto.getRandomValues if available
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;
    return [...buf].map((b, i) =>
      ([4,6,8,10].includes(i) ? '-' : '') + b.toString(16).padStart(2, '0')
    ).join('');
  }
  // Last resort: timestamp-based (no Math.random)
  return `${Date.now().toString(16)}-4000-8000-${performance.now().toString(16).replace('.', '')}`.padEnd(36, '0');
};

// ─── Custom Pie active-shape (enlarged slice + centred label) ─────────────────
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  return (
    <g>
      <text x={cx} y={cy - 12} textAnchor="middle" fill={fill} style={{ fontWeight: 700, fontSize: 13 }}>{payload.name}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#374151" style={{ fontSize: 12 }}>{`${(percent * 100).toFixed(1)}%`}</text>
      <text x={cx} y={cy + 28} textAnchor="middle" fill="#6b7280" style={{ fontSize: 11 }}>{value.toLocaleString()}</text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16} startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
};

// ─── Dimension filter badge ───────────────────────────────────────────────────
const DimensionBadge = ({ name, color, onClear }) => (
  <div
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-medium shadow-md"
    style={{ background: `linear-gradient(135deg, ${color}dd, ${color})` }}
  >
    <Filter className="w-3.5 h-3.5" />
    Filtered: {name}
    <button onClick={onClear} className="ml-1 hover:bg-white hover:bg-opacity-20 rounded-full p-0.5 transition-all">
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

DimensionBadge.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
};

// ─── Tooltip formatter extracted to avoid nested ternary ─────────────────────
const formatTooltipValue = (value, name, props) => {
  const pct = props?.payload?.percentage;
  const formattedPct = pct !== undefined && pct !== null ? pct.toFixed(1) : "N/A";
  return [`${value.toLocaleString()} (${formattedPct}%)`, name];
};

// ─── Filter Status Panel (extracted out of parent) ────────────────────────────
const FilterStatusPanel = ({ activeDimension, filteredStats, clearFilter }) => {
  if (!activeDimension || !filteredStats) return null;

};

FilterStatusPanel.propTypes = {
  activeDimension: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    index: PropTypes.number,
  }),
  filteredStats: PropTypes.shape({
    total: PropTypes.number,
    success: PropTypes.number,
    failed: PropTypes.number,
    successPct: PropTypes.number,
    failedPct: PropTypes.number,
  }),
  clearFilter: PropTypes.func.isRequired,
};

FilterStatusPanel.defaultProps = {
  activeDimension: null,
  filteredStats: null,
};

// ─── Filter Status Panel (extracted out of parent) ────────────────────────────
const FilterStatusPanelComponent = ({ activeDimension, filteredStats, clearFilter }) => {
  if (!activeDimension || !filteredStats) return null;

  const successClass = (() => {
    if (filteredStats.successPct >= 90) return "text-green-600";
    if (filteredStats.successPct >= 70) return "text-yellow-600";
    return "text-red-600";
  })();

  return (
    <div
      className="rounded-xl p-5 flex items-center justify-between shadow-sm"
      style={{
        background: `${activeDimension.color}0c`,
        border: `1px solid ${activeDimension.color}30`,
        borderLeft: `4px solid ${activeDimension.color}`,
      }}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <DimensionBadge name={activeDimension.name} color={activeDimension.color} onClear={clearFilter} />
        <div className="flex items-center gap-5 text-sm flex-wrap">
          <span className="text-gray-500">Records: <strong className="text-gray-800">{filteredStats.total?.toLocaleString()}</strong></span>
          <span className="text-green-600">Passed: <strong>{filteredStats.success?.toLocaleString()}</strong></span>
          <span className="text-red-500">Failed: <strong>{filteredStats.failed?.toLocaleString()}</strong></span>
          <span className={`font-semibold ${successClass}`}>
            Success: {filteredStats.successPct?.toFixed(1)}%
          </span>
        </div>
      </div>
      <button
        onClick={clearFilter}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-700 transition-all"
      >
        <X className="w-3.5 h-3.5" /> Show All
      </button>
    </div>
  );
};

FilterStatusPanelComponent.propTypes = {
  activeDimension: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    index: PropTypes.number,
  }).isRequired,
  filteredStats: PropTypes.shape({
    total: PropTypes.number,
    success: PropTypes.number,
    failed: PropTypes.number,
    successPct: PropTypes.number,
    failedPct: PropTypes.number,
  }).isRequired,
  clearFilter: PropTypes.func.isRequired,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Forecasting = () => {
  const [objects, setObjects] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedObject, setSelectedObject] = useState([]);
  const [selectedTable, setSelectedTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [dataSourceId, setDataSourceId] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [expandedError, setExpandedError] = useState(null);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [showObjectDropdown, setShowObjectDropdown] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [filters, setFilters] = useState({ columnName: "", issueType: "", tableName: "", severity: "", dimension: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [showDiscardPopup, setShowDiscardPopup] = useState(false);
  const [saveTitle, setSaveTitle] = useState(""); 
  const [lastThreadId, setLastThreadId] = useState(null); 
  const [currentUserId, setCurrentUserId] = useState(null);
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const navigate = useNavigate(); 

  // ── Interactive dimension filter state ────────────────────────────────────
  const [activeDimension, setActiveDimension] = useState(null);
  const [activePieIndex, setActivePieIndex] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_ISSUE_BASE_URL;


// ── Get current user email from MSAL session ─────────────────────────────
const getCurrentUserEmail = () => {
  try {
    const accountKey = JSON.parse(sessionStorage.getItem("msal.account.keys"))?.[0];
    if (accountKey) {
      const accountData = JSON.parse(sessionStorage.getItem(accountKey));
      return accountData?.username || null;
    }
    return null;
  } catch {
    return null;
  }
};

// ── Fetch userId by matching email against all users ─────────────────────
const fetchCurrentUserId = async () => {
  try {
    const currentUserEmail = getCurrentUserEmail();
    if (!currentUserEmail) return null;
    const response = await axiosClient.get("/api/User/all");
    const allUsers = Array.isArray(response?.data) ? response.data : [];
    const normalizedCurrentEmail = currentUserEmail.toLowerCase().trim();
    let matchedUser = allUsers.find(
      (user) => user.email?.toLowerCase().trim() === normalizedCurrentEmail
    );
    if (!matchedUser) {
      matchedUser = allUsers.find((user) => {
        if (!user.email) return false;
        const norm = user.email.toLowerCase().trim();
        return norm.includes(normalizedCurrentEmail) || normalizedCurrentEmail.includes(norm);
      });
    }
    return matchedUser?.userId || matchedUser?.id || null;
  } catch {
    return null;
  }
};
  // ── Fetch data source ID ──────────────────────────────────────────────────
  const fetchDataSourceId = async (configId) => {
    try {
      const response = await axiosClient.get(
        `api/MappingDB/CorpMappingDBByUserCorp?OrganizationConfigId=${configId}`
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) return data[0].dataSourceId;
      return null;
    } catch (err) {
      console.error("Error fetching dataSourceId:", err);
      return null;
    }
  };

  useEffect(() => {
    fetchObjects();
    const handleStorageChange = () => {
      fetchObjects();
      setSelectedObject([]);
      setSelectedTable([]);
      setTables([]);
      setForecastData(null);
      setDataSourceId(null);
    };
    globalThis.addEventListener("localStorageUpdated", handleStorageChange);
    globalThis.addEventListener("customStorageChange", handleStorageChange);
    return () => {
      globalThis.removeEventListener("localStorageUpdated", handleStorageChange);
      globalThis.removeEventListener("customStorageChange", handleStorageChange);
    };
  }, []);

  // ── Block navigation when forecastData exists ─────────────────────────────
useEffect(() => {

  // Block browser back/forward
  const handlePopState = (e) => {
    if (forecastData) {
      globalThis.history.pushState(null, "", globalThis.location.href);
      setShowDiscardPopup(true);
      setPendingNavPath(null);
    }
  };

  globalThis.history.pushState(null, "", globalThis.location.href);
  globalThis.addEventListener("popstate", handlePopState);
  return () => globalThis.removeEventListener("popstate", handlePopState);
}, [forecastData]);

  useEffect(() => {
    if (selectedObject.length > 0) {
      fetchTables(selectedObject);
      setSelectedTable([]);
      setForecastData(null);
    } else {
      setTables([]);
    }
  }, [selectedObject]);

  useEffect(() => {
    const handle = (e) => {
      if (showObjectDropdown && !e.target.closest(".object-dropdown-container")) setShowObjectDropdown(false);
      if (showTableDropdown && !e.target.closest(".table-dropdown-container")) setShowTableDropdown(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [showObjectDropdown, showTableDropdown]);

  // ── Reset dimension filter when new forecast is generated ─────────────────
  useEffect(() => {
    if (forecastData) {
      setActiveDimension(null);
      setActivePieIndex(null);
    }
  }, [forecastData]);

  // ── Intercept sidebar/internal link clicks ────────────────────────────────
useEffect(() => {
  if (!forecastData) return;
  const handleLinkClick = (e) => {
    const anchor = e.target.closest("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("http") || href === globalThis.location.pathname) return;
    e.preventDefault();
    e.stopPropagation();
    setPendingNavPath(href);
    setShowDiscardPopup(true);
  };
  document.addEventListener("click", handleLinkClick, true);
  return () => document.removeEventListener("click", handleLinkClick, true);
}, [forecastData]);

   // ── Fetch current userId on mount ─────────────────────────────────────────
  useEffect(() => {
    fetchCurrentUserId().then((id) => {
      if (id) setCurrentUserId(id);
    });
  }, []);

  const fetchObjects = async () => {
    setLoadingObjects(true);
    setError(null);
    try {
      const configId = localStorage.getItem("organization_config_id");
      if (!configId) {
        setError("Please select an organization configuration from the header");
        setObjects([]);
        setLoadingObjects(false);
        return;
      }
      const fetchedDataSourceId = await fetchDataSourceId(configId);
      setDataSourceId(fetchedDataSourceId);
      if (!fetchedDataSourceId) {
        setError("No data source found for the selected configuration");
        setObjects([]);
        setLoadingObjects(false);
        return;
      }
      const response = await axiosClientPayload.post(`${API_BASE_URL}/mapping-documents`, { organizationConfigId: configId });
      const data = response.data;
      const objectsList = Array.isArray(data) ? data : data.objects || data.data || [];
      setObjects(objectsList);
    } catch (err) {
      setError(err.message);
      setObjects([]);
    } finally {
      setLoadingObjects(false);
    }
  };

  const normalizeTablesData = (data) => {
    if (Array.isArray(data)) {
      return data;
    }
    if (data.target_structures) {
      return data.target_structures.map((t, i) => ({ 
        id: t, 
        name: t, 
        userMappingFileId: data.userMappingFileId?.[0] || `table-${i}` 
      }));
    }
    return data.tables || data.data || [];
  };

  const fetchTables = async (objectIds) => {
    setLoadingTables(true);
    setError(null);
    try {
      const response = await axiosClientPayload.post(`${API_BASE_URL}/target-tables`, { userMappingFileId: objectIds });
      const tablesList = normalizeTablesData(response.data);
      setTables(tablesList);
      if (tablesList.length === 0) setError("No tables found for the selected object");
    } catch (err) {
      setError(err.message);
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const runForecast = async () => {
    if (!selectedObject || !selectedTable) { setError("Please select both an object and a table"); return; }
    if (!dataSourceId) { setError("Data source ID not found. Please refresh and try again."); return; }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token") || "";
      const configId = localStorage.getItem("organization_config_id");
      const userId = currentUserId || localStorage.getItem("user_id");
      const newThreadId = generateUUID(); // ← generate once, reuse
      setLastThreadId(newThreadId);       // ← save for discard use
      const response = await axiosClientPayload.post(`${API_BASE_URL}/analyze`, {
        token,
        organizationConfigId: configId,
        userMappingFileId: selectedObject,
        targetStructures: selectedTable,
        dataSourceId,
        userId,
        threadId: newThreadId,
        title: saveTitle.trim() || "Untitled Analysis",
        action: "save",
      });
      setForecastData(response.data);
    } catch (err) {
      setError("Failed to generate forecast. Please try again.");
    } finally {
      setLoading(false);
    }
  };

   // ── Save title — calls update-title endpoint with last threadId ───────────
  const handleSaveTitle = async () => {
    try {
      const token = localStorage.getItem("token") || "";
      const configId = localStorage.getItem("organization_config_id");
      const userId = currentUserId || localStorage.getItem("user_id");

      if (lastThreadId && saveTitle.trim()) {
         await axiosClientPayload.post(`${API_BASE_URL}/thread-history/update-title`,
          {
            token,
            organizationConfigId: configId,
            userId,
            threadId: lastThreadId,   // ← same threadId from last analysis run
            title: saveTitle.trim(),
          }
        );
      }
    } catch (err) {
      console.error("Save title failed:", err);
    }
  };

  // ── Discard analysis — calls discard endpoint with last threadId ──────────
  const handleDiscard = async () => {
    try {
      const token = localStorage.getItem("token") || "";
      const configId = localStorage.getItem("organization_config_id");
      const userId = currentUserId || localStorage.getItem("user_id");

      if (lastThreadId) {
        await axiosClientPayload.post(`${API_BASE_URL}/thread-history/discard`, {
            token,
            organizationConfigId: configId,
            userId,
            threadId: lastThreadId,  // ← uses the saved threadId from last run
          }
        );
      }
    } catch (err) {
      console.error("Discard failed:", err);
    }
  };

  const handleGenerateForecast = async () => {
    if (forecastData) {
      setShowDiscardPopup(true);
      return;
    }
    await runForecast(); 
  };

  // ── Dimension interaction handlers ────────────────────────────────────────
  const handlePieClick = useCallback((data, index) => {
    const dimName = data?.name;
    if (!dimName) return;
    if (activeDimension?.name === dimName) {
      setActiveDimension(null);
      setActivePieIndex(null);
    } else {
      setActiveDimension({ name: dimName, color: getDimColor(index), index });
      setActivePieIndex(index);
    }
  }, [activeDimension]);

  const handleBarDimClick = useCallback((dimName, index) => {
    if (activeDimension?.name === dimName) {
      setActiveDimension(null);
      setActivePieIndex(null);
    } else {
      setActiveDimension({ name: dimName, color: getDimColor(index), index });
      setActivePieIndex(null);
    }
  }, [activeDimension]);

  const clearFilter = () => { setActiveDimension(null); setActivePieIndex(null); };

  // ── Build pie chart data with dimension index ─────────────────────────────
  const pieChartData = forecastData?.dimension_pie_chart?.map((dim, i) => ({
    name: dim.dimension,
    value: dim.count,
    percentage: dim.percentage,
    index: i,
  })) || [];

  // ── Derived filtered stats ────────────────────────────────────────────────
  const filteredStats = activeDimension
    ? (() => {
        const dim = forecastData?.dimension_bar_chart?.find((d) => d.dimension === activeDimension.name);
        if (!dim) return null;
        return {
          total: dim.success_records + dim.failed_records,
          success: dim.success_records,
          failed: dim.failed_records,
          successPct: dim.success_percentage,
          failedPct: dim.failed_percentage,
        };
      })()
    : null;

  // ── Helper to find matching issue record ─────────────────────────────────
  const findMatchingIssue = (errorItem) => {
    return forecastData?.issue_records_table_analysis?.find(
      (issue) => issue.column_name === errorItem.column_name && issue.issue_type === errorItem.rule
    );
  };

  // ── Helper to check dimension filter match ───────────────────────────────
  const checkDimensionFilter = (errorItem) => {
    if (!activeDimension) return true;
    const matchingIssue = findMatchingIssue(errorItem);
    return matchingIssue?.dimension === activeDimension.name;
  };

  // ── Helper to check dimension criteria match ─────────────────────────────
  const checkDimensionCriteria = (errorItem) => {
    if (!filters.dimension) return true;
    const matchingIssue = findMatchingIssue(errorItem);
    return matchingIssue?.dimension === filters.dimension;
  };

  const getFilteredErrors = useCallback(() => {
    if (!forecastData?.error_analysis) return [];
    return forecastData.error_analysis.filter((errorItem) => {
      const matchesDimensionFilter = checkDimensionFilter(errorItem);
      const matchesColumn = !filters.columnName || errorItem.column_name === filters.columnName;
      const matchesIssueType = !filters.issueType || errorItem.rule === filters.issueType;
      const matchesTable = !filters.tableName || errorItem.table_name === filters.tableName;
      const matchesSeverity = !filters.severity || errorItem.severity === filters.severity;
      const matchesDimension = checkDimensionCriteria(errorItem);
      return matchesDimensionFilter && matchesColumn && matchesIssueType && matchesTable && matchesSeverity && matchesDimension;
    });
  }, [forecastData, activeDimension, filters]);

  const clearFilters = () => setFilters({ columnName: "", issueType: "", tableName: "", severity: "", dimension: "" });
  const activeFiltersCount = Object.values(filters).filter((v) => v !== "").length;

  const getUniqueColumnNames = () => [...new Set(forecastData?.error_analysis?.map((e) => e.column_name) || [])].sort((a, b) => a.localeCompare(b));
  const getUniqueIssueTypes = () => [...new Set(forecastData?.error_analysis?.map((e) => e.rule) || [])].sort((a, b) => a.localeCompare(b));
  const getUniqueTableNames = () => [...new Set(forecastData?.error_analysis?.map((e) => e.table_name) || [])].sort((a, b) => a.localeCompare(b));
  const getUniqueDimensions = () => [...new Set((forecastData?.issue_records_table_analysis?.map((e) => e.dimension) || []).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const handleSelectAllObjects = (selectAll) => {
    setSelectedObject(selectAll ? objects.map((o) => o.userMappingFileId) : []);
  };
  const handleSelectAllTables = (selectAll) => {
    setSelectedTable(selectAll ? tables.map((t) => t.id || t.name) : []);
  };

  // ── Get object dropdown label text ─────────────────────────────────────────
  const getObjectDropdownLabel = () => {
    if (selectedObject.length === 0) {
      if (loadingObjects) return "Loading objects...";
      if (objects.length === 0) return "No objects available";
      return "Choose objects";
    }
    return `${selectedObject.length} object(s) selected`;
  };

  // ── Get table dropdown label text ──────────────────────────────────────────
  const getTableDropdownLabel = () => {
    if (selectedTable.length === 0) {
      if (loadingTables) return "Loading tables...";
      if (selectedObject.length === 0) return "Select objects first";
      if (tables.length === 0) return "No tables available";
      return "Choose tables";
    }
    return `${selectedTable.length} table(s) selected`;
  };

  // ── Overview card data (reactive to filter) ───────────────────────────────
  const overallAnalysis = forecastData?.overall_analysis;
  const cards = [
    {
      label: "Total Records",
      icon: <Database className="w-5 h-5 text-blue-600" />,
      value: (activeDimension ? filteredStats?.total : overallAnalysis?.total_records)?.toLocaleString() || "N/A",
      sub: activeDimension ? `Dimension: ${activeDimension.name}` : `${overallAnalysis?.total_columns_analyzed || 0} columns analyzed`,
      g: "from-blue-50 to-blue-100", b: "border-blue-200", lc: "text-blue-700", vc: "text-blue-900", sc: "text-blue-600",
    },
    {
      label: "Success Rate",
      icon: <TrendingUp className="w-5 h-5 text-green-600" />,
      value: activeDimension ? `${filteredStats?.successPct?.toFixed(1) || "N/A"}%` : `${overallAnalysis?.overall_success_percentage || "N/A"}%`,
      sub: activeDimension ? `${filteredStats?.success?.toLocaleString() || 0} records passed` : `${overallAnalysis?.total_success_records?.toLocaleString() || 0} records passed`,
      g: "from-green-50 to-green-100", b: "border-green-200", lc: "text-green-700", vc: "text-green-900", sc: "text-green-600",
    },
    {
      label: "Failed Records",
      icon: <AlertCircle className="w-5 h-5 text-red-600" />,
      value: (activeDimension ? filteredStats?.failed : overallAnalysis?.total_failed_records)?.toLocaleString() || "N/A",
      sub: activeDimension ? `${filteredStats?.failedPct?.toFixed(1) || 0}% failure rate` : `${overallAnalysis?.overall_failed_percentage || 0}% failure rate`,
      g: "from-red-50 to-red-100", b: "border-red-200", lc: "text-red-700", vc: "text-red-900", sc: "text-red-600",
    },
    {
      label: "Issues Found",
      icon: <Table className="w-5 h-5 text-orange-600" />,
      value: activeDimension
        ? getFilteredErrors().length.toString()
        : (overallAnalysis?.columns_with_issues?.toString() || "N/A"),
      sub: activeDimension ? "Filtered issues" : `${overallAnalysis?.total_rules_applied || 0} rules applied`,
      g: "from-orange-50 to-orange-100", b: "border-orange-200", lc: "text-orange-700", vc: "text-orange-900", sc: "text-orange-600",
    },
  ];

  // ── Helper to determine legend button class ─────────────────────────────
  const getLegendButtonClass = (isActive, hasActiveDimension) => {
    if (isActive) {
      return "shadow-md scale-105 border-current";
    }
    if (hasActiveDimension) {
      return "opacity-35 border-gray-200 hover:opacity-60";
    }
    return "border-transparent hover:border-gray-200 hover:bg-gray-50";
  };

  // ── Custom clickable legend ───────────────────────────────────────────────
  const renderClickableLegend = (labels) => (
    <div className="flex flex-wrap justify-center gap-2 mt-2">
      {labels.map((label, index) => {
        const isActive = activeDimension?.name === label;
        const color = getDimColor(index);
        const buttonClass = getLegendButtonClass(isActive, !!activeDimension);
        return (
          <button
            key={label}
            onClick={() => handlePieClick({ name: label }, index)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${buttonClass}`}
            style={{ color }}
          >
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
            {label}
          </button>
        );
      })}
    </div>
  );

  // ── Severity badge class helper ───────────────────────────────────────────
  const getSeverityClass = (severity) => {
    if (severity === "high") return "bg-red-100 text-red-800";
    if (severity === "medium") return "bg-yellow-100 text-yellow-800";
    return "bg-blue-100 text-blue-800";
  };

  // ── Insight type badge class helper ──────────────────────────────────────
  const getInsightBadgeClass = (insightType) => {
    if (insightType === "Critical") return "bg-red-100 text-red-800";
    if (insightType === "Warning") return "bg-yellow-100 text-yellow-800";
    return "bg-blue-100 text-blue-800";
  };

  // ── Insight panel border class helper ────────────────────────────────────
  const getInsightPanelClass = (insightType) => {
    if (insightType === "Critical") return "bg-red-50 border-red-500";
    if (insightType === "Warning") return "bg-yellow-50 border-yellow-500";
    return "bg-blue-50 border-blue-500";
  };

  // ── Bar chart item style helper ───────────────────────────────────────────
  const getBarItemStyle = (isActive, color) => {
    if (!isActive) return {};
    return { ringColor: color, background: `${color}08`, border: `1px solid ${color}30` };
  };

  return (
    <div className="w-full space-y-6 p-6">

      {/* ── Selection Panel ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-800 flex items-start gap-2">
            <Database className="w-5 h-5 text-blue-600 mt-1" />
            <div className="flex flex-col">
              <span>Issue Predict Insights</span>
              <span className="text-sm font-normal text-gray-500">AI-driven analysis to forecast issues before they impact operations</span>
            </div>
          </h2>
          <button onClick={fetchObjects} disabled={loadingObjects}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all disabled:opacity-50"
            title="Refresh objects">
            <RefreshCw className={`w-4 h-4 ${loadingObjects ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4 items-end">
          {/* Object Dropdown */}
          <div className="relative flex-1 w-full object-dropdown-container">
            <label htmlFor="object-select-btn" className="block text-sm font-medium text-gray-700 mb-2">Select Objects</label>
            <button
              id="object-select-btn"
              type="button"
              onClick={() => setShowObjectDropdown(!showObjectDropdown)}
              disabled={loadingObjects || objects.length === 0}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between"
            >
              <span className="text-sm text-gray-700">
                {getObjectDropdownLabel()}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showObjectDropdown ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showObjectDropdown && objects.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                <div className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                  <label className="flex items-center px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                    <input type="checkbox"
                      checked={selectedObject.length === objects.length && objects.length > 0}
                      onChange={(e) => handleSelectAllObjects(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    <span className="ml-3 text-sm font-semibold text-gray-900">Select All ({objects.length})</span>
                  </label>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {objects.map((obj) => (
                    <label key={obj.userMappingFileId} className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={selectedObject.includes(obj.userMappingFileId)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedObject([...selectedObject, obj.userMappingFileId]);
                          else setSelectedObject(selectedObject.filter((id) => id !== obj.userMappingFileId));
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                      <span className="ml-3 text-sm text-gray-700">{obj.uploadedObjDescription || obj.uploadedObjName || "Unnamed Object"}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Table Dropdown */}
          <div className="relative flex-1 w-full table-dropdown-container">
            <label htmlFor="table-select-btn" className="block text-sm font-medium text-gray-700 mb-2">Select Tables</label>
            <button
              id="table-select-btn"
              type="button"
              onClick={() => setShowTableDropdown(!showTableDropdown)}
              disabled={selectedObject.length === 0 || loadingTables || tables.length === 0}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between"
            >
              <span className="text-sm text-gray-700">
                {getTableDropdownLabel()}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showTableDropdown ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showTableDropdown && tables.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                <div className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                  <label className="flex items-center px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                    <input type="checkbox"
                      checked={selectedTable.length === tables.length && tables.length > 0}
                      onChange={(e) => handleSelectAllTables(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    <span className="ml-3 text-sm font-semibold text-gray-900">Select All ({tables.length})</span>
                  </label>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {tables.map((table) => (
                    <label key={table.id || table.name} className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={selectedTable.includes(table.id || table.name)}
                        onChange={(e) => {
                          const tableId = table.id || table.name;
                          if (e.target.checked) setSelectedTable([...selectedTable, tableId]);
                          else setSelectedTable(selectedTable.filter((id) => id !== tableId));
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                      <span className="ml-3 text-sm text-gray-700">{table.name || table.uploadedFileName || table.uploadedObjName || "Unnamed Table"}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Generate Button */}
          <div className="w-full lg:w-auto">
            <button onClick={handleGenerateForecast}
              disabled={selectedObject.length === 0 || selectedTable.length === 0 || loading}
              className="w-full lg:w-auto px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 whitespace-nowrap">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Generating Forecast...</>
              ) : (
                <><TrendingUp className="w-4 h-4" />Generate Forecast</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Analysis Results ────────────────────────────────────────────────── */}
      {forecastData && (
        <div className="space-y-6">

          {/* Header */}
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Analysis Results</h2>
            <button onClick={() => setShowAIInsights(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all shadow-md hover:shadow-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              AI Insights
            </button>
          </div>

          {/* Active filter banner */}
          <FilterStatusPanelComponent
            activeDimension={activeDimension}
            filteredStats={filteredStats}
            clearFilter={clearFilter}
          />

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map((c) => (
              <div key={c.label} className={`bg-gradient-to-br ${c.g} rounded-xl shadow-sm border ${c.b} p-6 transition-all duration-300`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-semibold ${c.lc}`}>{c.label}</span>
                  {c.icon}
                </div>
                <p className={`text-3xl font-bold ${c.vc}`}>{c.value}</p>
                <p className={`text-sm ${c.sc} mt-2`}>{c.sub}</p>
              </div>
            ))}
          </div>

          {/* ── Charts ──────────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Pie Chart */}
            {pieChartData.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-1">
                  <h3 className="text-lg font-semibold text-gray-800">Dimension Distribution</h3>
                  {activeDimension && (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium whitespace-nowrap">Click again to reset</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mb-3">Click a slice or legend item to filter the entire dashboard</p>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={90}
                      activeIndex={activePieIndex}
                      activeShape={renderActiveShape}
                      onClick={(data, index) => handlePieClick(data, index)}
                      style={{ cursor: "pointer" }}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${entry.name}`}
                          fill={getDimColor(index)}
                          opacity={activeDimension && activeDimension.name !== entry.name ? 0.2 : 1}
                          style={{ transition: "opacity 0.25s ease" }}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: "rgba(255,255,255,0.95)", border: "1px solid #e5e7eb", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                      formatter={formatTooltipValue}
                    />
                    <Legend content={() => renderClickableLegend(pieChartData.map((d) => d.name))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Bar Chart (custom horizontal bars, interactive) */}
            {forecastData.dimension_bar_chart?.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-1">
                  <h3 className="text-base font-semibold text-gray-900">Success vs Failed by Dimension</h3>
                  {activeDimension && (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-medium whitespace-nowrap">Click again to reset</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mb-5">Click a dimension bar to filter the entire dashboard</p>

                <div className="space-y-5">
                  {forecastData.dimension_bar_chart.map((dim, idx) => {
                    const isActive = activeDimension?.name === dim.dimension;
                    const isDimmed = activeDimension && !isActive;
                    const color = getDimColor(idx);
                    const barItemStyle = getBarItemStyle(isActive, color);
                    const nameLabelClass = isActive ? "font-bold" : "text-gray-800";
                    const nameLabelStyle = isActive ? { color } : {};

                    return (
                      <div
                        key={dim.dimension}
                        onClick={() => handleBarDimClick(dim.dimension, idx)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleBarDimClick(dim.dimension, idx); }}
                        role="button"
                        tabIndex={0}
                        className={`space-y-2 rounded-xl p-3 cursor-pointer transition-all duration-200 ${
                          isActive
                            ? "shadow-md ring-2 ring-offset-1"
                            : isDimmed
                            ? "opacity-30"
                            : "hover:bg-gray-50"
                        }`}
                        style={barItemStyle}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                            <span className={`text-sm font-medium ${nameLabelClass}`} style={nameLabelStyle}>
                              {dim.dimension}
                            </span>
                          </div>
                          <span className={`text-sm font-semibold ${dim.success_percentage >= 90 ? "text-emerald-600" : dim.success_percentage >= 70 ? "text-yellow-600" : "text-red-600"}`}>
                            {dim.success_percentage.toFixed(1)}%
                          </span>
                        </div>

                        <div className="relative h-3 w-full rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="absolute left-0 top-0 h-full rounded-l-full transition-all duration-500"
                            style={{ width: `${dim.success_percentage}%`, background: "#10b981" }}
                          />
                          <div
                            className="absolute right-0 top-0 h-full rounded-r-full transition-all duration-500"
                            style={{ width: `${dim.failed_percentage}%`, background: "#ef4444" }}
                          />
                        </div>

                        <div className="flex justify-between text-xs text-gray-500">
                          <span>
                            <span className="font-medium text-emerald-600">Success:</span>{" "}
                            {dim.success_records.toLocaleString()}
                          </span>
                          <span>
                            <span className="font-medium text-rose-500">Failed:</span>{" "}
                            {dim.failed_records.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Issues Table ─────────────────────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-800">Issues Detected</h3>
                    {activeDimension && (
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: activeDimension.color }} />
                        <span className="text-sm font-medium text-gray-600">
                          Showing: <strong style={{ color: activeDimension.color }}>{activeDimension.name}</strong>
                        </span>
                        <button onClick={clearFilter} className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors">Clear</button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {getFilteredErrors().length} of {forecastData.error_analysis?.length || 0} issues
                    {(activeDimension || activeFiltersCount > 0) && ` (filtered)`}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {(activeFiltersCount > 0 || activeDimension) && (
                    <button
                      onClick={() => { clearFilters(); clearFilter(); }}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <X className="w-4 h-4" /> Clear All Filters
                    </button>
                  )}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${showFilters ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                  >
                    <Filter className="w-4 h-4" />
                    {showFilters ? "Hide Filters" : "Show Filters"}
                    {activeFiltersCount > 0 && (
                      <span className="bg-white text-blue-600 text-xs font-bold px-2 py-0.5 rounded-full">{activeFiltersCount}</span>
                    )}
                  </button>
                </div>
              </div>

              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[
                    { label: "Column Name", key: "columnName", options: getUniqueColumnNames(), placeholder: "All Columns" },
                    { label: "Issue Type", key: "issueType", options: getUniqueIssueTypes(), placeholder: "All Issue Types" },
                    { label: "Table Name", key: "tableName", options: getUniqueTableNames(), placeholder: "All Tables" },
                    { label: "Severity", key: "severity", options: ["high", "medium", "low"], placeholder: "All Severities" },
                    { label: "Dimension", key: "dimension", options: getUniqueDimensions(), placeholder: "All Dimensions" },
                  ].map(({ label, key, options, placeholder }) => (
                    <div key={key}>
                      <label htmlFor={`filter-${key}`} className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                      <select
                        id={`filter-${key}`}
                        value={filters[key]}
                        onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">{placeholder}</option>
                        {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {["Column", "Issue Type", "Table Name", "Records", "Severity", "Dimension", "Source Table Name", "Source Field Name", "Default Value", "Action"].map((h) => (
                      <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === "Action" ? "text-center" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredErrors().length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-6 py-12 text-center text-gray-400 text-sm">
                        {activeDimension ? `No issues found for dimension: ${activeDimension.name}` : "No issues detected"}
                      </td>
                    </tr>
                  ) : (
                    getFilteredErrors().map((errorItem, idx) => {
                      const matchingIssue = forecastData.issue_records_table_analysis?.find(
                        (issue) => issue.column_name === errorItem.column_name && issue.issue_type === errorItem.rule
                      );
                      const dimension = matchingIssue?.dimension || "-";
                      const dimIndex = forecastData.dimension_bar_chart?.findIndex((d) => d.dimension === dimension) ?? 0;
                      const dimColor = dimension === "-" ? "#9ca3af" : getDimColor(dimIndex);
                      const rowHighlighted = activeDimension && matchingIssue?.dimension === activeDimension.name;

                      return (
                        <React.Fragment key={`${errorItem.column_name}-${errorItem.rule}-${idx}`}>
                          <tr className={`transition-colors ${rowHighlighted ? "bg-blue-50" : "hover:bg-gray-50"}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{errorItem.column_name}</div>
                              {errorItem.mandatory === "Y" && <span className="text-xs text-red-600 font-medium">Mandatory</span>}
                            </td>
                            <td className="px-6 py-4"><div className="text-sm text-gray-900">{errorItem.rule}</div></td>
                            <td className="px-6 py-4"><div className="text-sm text-gray-500">{errorItem.table_name}</div></td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                <span className="font-medium text-red-600">{errorItem.issue_records.toLocaleString()}</span>
                                <span className="text-gray-400"> / </span>
                                <span className="text-gray-600">{errorItem.total_records.toLocaleString()}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">{errorItem.failed_percentage.toFixed(1)}% failed</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityClass(errorItem.severity)}`}>
                                {errorItem.severity}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {dimension === "-" ? (
                                <span className="text-gray-400 text-sm">-</span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                                  style={{ background: `${dimColor}15`, color: dimColor, border: `1px solid ${dimColor}40` }}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dimColor }} />
                                  {dimension}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4"><div className="text-sm text-gray-900">{errorItem.source_table === "-" ? <span className="text-gray-400">-</span> : errorItem.source_table}</div></td>
                            <td className="px-6 py-4"><div className="text-sm text-gray-900">{matchingIssue?.source_field_name || <span className="text-gray-400">-</span>}</div></td>
                            <td className="px-6 py-4"><div className="text-sm text-gray-900">{errorItem.default_value === "-" ? <span className="text-gray-400">-</span> : errorItem.default_value}</div></td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <button
                                onClick={() => setExpandedError(expandedError === idx ? null : idx)}
                                className="text-blue-600 hover:text-blue-900 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center justify-center"
                              >
                                {expandedError === idx ? "Close" : "Details"}
                              </button>
                            </td>
                          </tr>

                          {expandedError === idx && (
                            <tr>
                              <td colSpan={10} className="px-0 py-0">
                                <div className="bg-gray-50 border-t border-b border-gray-200">
                                  <div className="p-6 space-y-4">
                                    <div className="flex items-start justify-between">
                                      <h4 className="text-lg font-semibold text-gray-900">Issue Details: {errorItem.column_name}</h4>
                                      <button onClick={() => setExpandedError(null)} className="text-gray-400 hover:text-gray-600">
                                        <X className="w-5 h-5" />
                                      </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                                        <div className="text-sm font-medium text-gray-500 mb-2">What Went Wrong</div>
                                        <p className="text-sm text-gray-700">{errorItem.what_went_wrong}</p>
                                      </div>
                                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                                        <div className="text-sm font-medium text-gray-500 mb-2">Expected Behavior</div>
                                        <p className="text-sm text-gray-700">{errorItem.what_should_be}</p>
                                      </div>
                                    </div>

                                    {errorItem.ai_insight && (
                                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <div className="text-sm font-medium text-blue-700 mb-1">AI Insight</div>
                                        <p className="text-sm text-blue-900">{errorItem.ai_insight}</p>
                                      </div>
                                    )}

                                    {errorItem.failed_samples?.length > 0 && (
                                      <div>
                                        <div className="text-sm font-medium text-gray-700 mb-2">Sample Failed Values</div>
                                        <div className="flex flex-wrap gap-2">
                                          {errorItem.failed_samples.slice(0, 10).map((sample, sIdx) => (
                                            <span key={`${sample}-${sIdx}`} className="px-3 py-1 bg-red-50 text-red-700 text-sm rounded-md border border-red-200 font-mono">{sample}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* SQL Fix */}
                                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                                            </svg>
                                            <span className="text-sm font-semibold text-blue-900">SQL Fix</span>
                                          </div>
                                          <button onClick={() => navigator.clipboard.writeText(errorItem.sql_fix)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-200 p-1.5 rounded transition-colors" title="Copy SQL">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                          </button>
                                        </div>
                                        <SyntaxHighlighter language="sql" style={vscDarkPlus} customStyle={{ margin: 0, padding: "1rem", fontSize: "0.75rem", lineHeight: "1.5", borderRadius: 0 }} showLineNumbers wrapLines>{errorItem.sql_fix}</SyntaxHighlighter>
                                      </div>

                                      {/* Python Fix */}
                                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                        <div className="bg-gradient-to-r from-green-50 to-green-100 px-4 py-3 border-b border-green-200 flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                            </svg>
                                            <span className="text-sm font-semibold text-green-900">Python Fix</span>
                                          </div>
                                          <button onClick={() => navigator.clipboard.writeText(errorItem.python_fix)} className="text-green-600 hover:text-green-800 hover:bg-green-200 p-1.5 rounded transition-colors" title="Copy Python">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                          </button>
                                        </div>
                                        <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ margin: 0, padding: "1rem", fontSize: "0.75rem", lineHeight: "1.5", borderRadius: 0 }} showLineNumbers wrapLines>{errorItem.python_fix}</SyntaxHighlighter>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Insights Modal ───────────────────────────────────────────────── */}
      {showAIInsights && (
        <div className="fixed inset-0 z-[9999]" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}>
          <button
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowAIInsights(false)}
            onKeyDown={(e) => { if (e.key === "Escape") setShowAIInsights(false); }}
            aria-label="Close AI Insights"
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, width: "100vw", height: "100vh" }}
            type="button"
          />
          <div className="absolute inset-0 flex items-center justify-center p-4" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative z-10">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <h3 className="text-2xl font-bold">AI-Powered Insights</h3>
                  </div>
                  <button onClick={() => setShowAIInsights(false)} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-purple-100 mt-2">Automated analysis and recommendations for your data</p>
              </div>

              <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 140px)" }}>
                {forecastData?.overall_ai_insights?.length > 0 ? (
                  <div className="space-y-4">
                    {forecastData.overall_ai_insights.map((insight) => (
                      <div key={`insight-${insight.title}-${insight.priority}`} className={`p-5 rounded-xl border-l-4 ${getInsightPanelClass(insight.insight_type)}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {insight.insight_type === "Critical" && <AlertCircle className="w-5 h-5 text-red-600" />}
                            {insight.insight_type === "Warning" && <AlertCircle className="w-5 h-5 text-yellow-600" />}
                            {insight.insight_type === "Info" && (
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${getInsightBadgeClass(insight.insight_type)}`}>{insight.insight_type}</span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">Priority {insight.priority}</span>
                        </div>
                        <h4 className="font-bold text-gray-900 text-lg mb-2">{insight.title}</h4>
                        <p className="text-gray-700 mb-3 leading-relaxed">{insight.description}</p>
                        {insight.affected_columns?.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-medium text-gray-600 mb-2">Affected Columns:</div>
                            <div className="flex flex-wrap gap-2">
                              {insight.affected_columns.map((col) => (
                                <span key={`col-${col}`} className="text-xs px-2 py-1 bg-white border border-gray-200 rounded text-gray-700 font-mono">{col}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="bg-white bg-opacity-60 p-3 rounded-lg border border-gray-200">
                          <div className="text-sm font-medium text-gray-700 mb-1">Recommendation:</div>
                          <p className="text-sm text-gray-800">{insight.recommendation}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <p className="text-gray-500">No AI insights available for this analysis</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
   {/* ── Discard & Regenerate Popup ─────────────────────────────────────── */}
      {showDiscardPopup && (
  <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, width: "100vw", height: "100vh", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.5)", margin: 0, padding: 0, inset: 0 }}>
    <button
      className="absolute inset-0"
      onClick={() => { setShowDiscardPopup(false); setSaveTitle(""); }}
      onKeyDown={(e) => { if (e.key === "Escape") { setShowDiscardPopup(false); setSaveTitle(""); } }}
      aria-label="Close discard popup"
      type="button"
    />
    <div style={{ position: "relative", backgroundColor: "white", borderRadius: "0.75rem", boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: "1.75rem", maxWidth: "22rem", width: "100%", margin: "0 1rem", zIndex: 100000 }}>

      {/* Icon + Title */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-base font-bold text-gray-900">Save Analysis Before Leaving?</h3>
      </div>

      {/* Subtitle */}
      <p className="text-sm text-gray-500 mb-4 leading-relaxed">
        You have unsaved changes in your analysis. Would you like to save them before exiting?
      </p>

      {/* Title input */}
      <div className="mb-5">
        <label htmlFor="analysis-title-input" className="block text-sm font-medium text-gray-700 mb-1.5">
  Analysis Name <span className="text-red-500">*</span>
</label>
        <input
          id="analysis-title-input"
          type="text"
          value={saveTitle}
          onChange={(e) => setSaveTitle(e.target.value)}
          placeholder="Enter a name for your analysis"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <button
         onClick={async () => {
  setShowDiscardPopup(false);
  await handleSaveTitle();
  toast.success(`✅ Analysis "${saveTitle.trim()}" saved successfully!`);
  if (pendingNavPath) {
    navigate(pendingNavPath);
    setPendingNavPath(null);
  }
}}
          disabled={saveTitle.trim() === ""}
          className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            saveTitle.trim().length > 0
              ? "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          Save & Continue
        </button>
        <button
          onClick={async () => {
  setShowDiscardPopup(false);
  setSaveTitle("");
  setForecastData(null);
  await handleDiscard();
  toast.error("❌ Analysis discarded.");
  if (pendingNavPath) {
    navigate(pendingNavPath);
    setPendingNavPath(null);
  }
}}
          className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-medium text-sm"
        >
          Discard Changes
        </button>
      </div>
    </div>
  </div>
)}
    </div>   
  );
};

export default Forecasting;